// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';

import {
  FHAntdDateTimePicker,
  FHAutocomplete,
  FHButton,
  FHConfirmDialog,
  FHSelect,
} from '../../../../components/base';
import { clientAssessmentActions } from '../../../../store';
import { FORTA_BLUE, FORTA_GRAY3, FORTA_WHITE } from '../../../../utils/colors';
import { getUtcOffset } from '../../../../utils/common';
import { createClientAssessmentSchema } from './validationSchema';
import {
  areUserRolesIn,
  capitalizeFirstLetter,
  disabledDatesOutsideRange,
  disabledFutureHoursAndMinutes,
  getAllInvalidFieldNames,
  history,
} from '../../../../helpers';
import {
  AssessmentNoteStatus,
  BackgroundTypes,
  ClinicalRelationships,
  Roles,
} from '../../../../constants';
import { CLIENT_ASSESSMENT_DURATION_OPTIONS } from './constants';
import { SUPERVISION_LOCATION_V2, SUPERVISION_SETTING_V2 } from '../constants';
import TrashIcon from '../../../svgs/TrashIcon3';
import { ClinicalStatus } from '../../../../admin/constants/general';

import type { IClient, IClientAssessment, IOption, SelectValueType } from '../../../../utils/types';
import type { RootState } from '../../../../store';
import type { Moment } from 'moment';
import type { ChangeEvent, HTMLAttributes } from 'react';

interface IClientAssessmentCreatePayload {
  date_start: string;
  utc_offset_date_start: number;
  client_id: number;
  duration: number;
  location: string;
  delivery_method: string;
}

interface IClientAssessmentInfoProps {
  clientAssessment?: IClientAssessment;
  buttonDisabled: boolean;
}

export function ClientAssessmentInfo({
  clientAssessment,
  buttonDisabled,
}: IClientAssessmentInfoProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);
  const { list: clients = [] } = useSelector((state: RootState) => state.clients);
  const { loading: clientAssessmentLoading } = useSelector(
    (state: RootState) => state.clientAssessment,
  );
  const { list: providerAssignments = [] } = useSelector(
    (state: RootState) => state.providerAssignment,
  );
  const [selectedClientId, setSelectedClientId] = useState<number | string>('');
  const [startDateTime, setStartDateTime] = useState<Moment | string>('');
  const [duration, setDuration] = useState<number | string>('');
  const [clientLocation, setClientLocation] = useState<string>('');
  const [deliveryMethod, setDeliveryMethod] = useState<string>('');
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);

  const isAssessmentIncomplete = clientAssessment?.status !== AssessmentNoteStatus.Completed;

  const showDeleteButton = useMemo(() => {
    return Boolean(
      clientAssessment?.id &&
      (clientAssessment.provider.email === user?.email ||
        areUserRolesIn(user?.roles, [Roles.Admin])),
    );
  }, [clientAssessment, isAssessmentIncomplete, user]);

  const clientOptions = useMemo(() => {
    if (!clients) return [];

    return areUserRolesIn(user?.roles, [Roles.Bcba])
      ? (providerAssignments
          ?.filter((pa) => pa.client?.clinical_status === ClinicalStatus.CLINICAL)
          ?.map((pa) => {
            if (pa.client.id == null) return null;
            return {
              id: pa.client.id,
              label: `${pa.client.first_name} ${pa.client.last_name}`,
              description:
                pa.clinical_relationship === ClinicalRelationships.HomeBcba
                  ? ''
                  : pa.clinical_relationship?.split('_')[0],
            };
          })
          .filter((option): option is IOption => option !== null)
          .sort((a, b) => a.label.localeCompare(b.label)) ?? [])
      : clients
          .filter((client): client is IClient & { id: number } => client.id != null)
          .map((client) => ({
            id: client.id,
            label: `${client.first_name} ${client.last_name}`,
          }));
  }, [user, clients, providerAssignments]);

  useEffect(() => {
    if (!(clientAssessment != null && Object.hasOwn(clientAssessment, 'id'))) {
      return;
    }
    setSelectedClientId(clientAssessment.client_id);
    setStartDateTime(moment(clientAssessment.date_start));
    setDuration(clientAssessment.duration);
    setClientLocation(clientAssessment.location);
    setDeliveryMethod(clientAssessment.delivery_method);
  }, [clientAssessment]);

  const handleUpdateDuration = async (value: number) => {
    setDuration(value);
    if (!clientAssessment) return;
    const res = await dispatch(
      clientAssessmentActions.updateAssessmentNote({
        data: {
          ...clientAssessment,
          duration: value,
        },
        assessmentNoteId: clientAssessment?.id,
        getAccessTokenSilently,
      }),
    );
    if (!res.payload) {
      return;
    }
    toast.success('Duration has been updated successfully');
  };

  const handleUpdateDateTime = async (startDate: Moment) => {
    if (
      !clientAssessment ||
      moment(clientAssessment?.date_start).isSame(moment(startDate), 'minute')
    )
      return;

    const res = await dispatch(
      clientAssessmentActions.updateAssessmentNote({
        data: {
          ...clientAssessment,
          date_start: startDate.toISOString(),
          utc_offset_date_start: getUtcOffset(moment(new Date())),
        },
        assessmentNoteId: clientAssessment?.id,
        getAccessTokenSilently,
      }),
    );

    if (!res.payload) {
      return;
    }
    setStartDateTime(startDate);
    toast.success('Start date and/or the time has been changed successfully');
  };

  const handleDateTimePickerChange = async (newValue: Moment) => {
    if (moment(newValue, 'minute').isAfter(moment(new Date()), 'minute')) {
      const resetValue = moment(newValue).hours(0).minutes(0).seconds(0).milliseconds(0);
      setStartDateTime(resetValue);
    } else {
      setStartDateTime(newValue);
    }
  };

  const validate = (data: IClientAssessmentCreatePayload) => {
    return getAllInvalidFieldNames(createClientAssessmentSchema, data);
  };

  const handleSubmit = async () => {
    if (!moment.isMoment(startDateTime) || selectedClientId === '') return;
    const clientId = Number(selectedClientId);
    const assessmentDuration = Number(duration);
    if (!Number.isInteger(clientId) || !Number.isFinite(assessmentDuration)) return;

    const data: IClientAssessmentCreatePayload = {
      date_start: startDateTime.toISOString(),
      utc_offset_date_start: getUtcOffset(moment(new Date())),
      client_id: clientId,
      duration: assessmentDuration,
      location: clientLocation,
      delivery_method: deliveryMethod,
    };

    const errors = validate(data);
    if (Object.keys(errors).length) return;
    const response = await dispatch(
      clientAssessmentActions.createAssessmentNote({ getAccessTokenSilently, data }),
    );
    if (response?.payload != null && Object.hasOwn(response?.payload, 'id')) {
      window.history.replaceState(null, '', `/assessment-note/${response.payload.id}`);
    }
  };

  const handleDeleteAssessment = async () => {
    if (!clientAssessment?.id) return;
    setShowConfirmDeleteDialog(false);

    const { error } = await dispatch(
      clientAssessmentActions.deleteAssessmentNote({
        assessmentNoteId: clientAssessment.id,
        getAccessTokenSilently,
      }),
    );

    if (error) return;
    history.navigate('/dashboard');
  };

  const handleUpdateClientLocation = async (value: string) => {
    setClientLocation(value);
    if (!clientAssessment) return;
    const res = await dispatch(
      clientAssessmentActions.updateAssessmentNote({
        data: {
          ...clientAssessment,
          location: value,
        },
        assessmentNoteId: clientAssessment?.id,
        getAccessTokenSilently,
      }),
    );
    if (!res.payload) {
      return;
    }
    toast.success('Client location has been updated successfully');
  };

  const handleUpdateDeliveryMethod = async (value: string) => {
    setDeliveryMethod(value);
    if (!clientAssessment) return;
    const res = await dispatch(
      clientAssessmentActions.updateAssessmentNote({
        data: {
          ...clientAssessment,
          delivery_method: value,
        },
        assessmentNoteId: clientAssessment?.id,
        getAccessTokenSilently,
      }),
    );
    if (!res.payload) {
      return;
    }
    toast.success('Delivery method has been updated successfully');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Grid container spacing={2}>
        <Grid item md={2.5} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Client name
          </Typography>
          <FHAutocomplete
            data-testid="clientName"
            sx={{
              flexGrow: 1,
              boxShadow: 'none',
              '& .MuiInputBase-root': {
                height: '40px',
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
            inputHeight={40}
            coloredBackground
            placeholder="Select client"
            clearIcon={false}
            disabled={Boolean(clientAssessment?.id)}
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => {
              return (
                <Box
                  {...props}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                  key={option.id}
                  data-testid={`clientLabel${option.id}`}
                >
                  <Typography>{option.label}</Typography>
                  {option.description && (
                    <Typography
                      sx={{
                        px: 1,
                        ml: 1,
                        borderRadius: '4px',
                        color: FORTA_WHITE,
                        fontWeight: 700,
                        backgroundColor: FORTA_BLUE.light1,
                      }}
                    >
                      {capitalizeFirstLetter(option.description)}
                    </Typography>
                  )}
                </Box>
              );
            }}
            options={clientOptions ?? []}
            value={
              clientOptions.length > 0 && selectedClientId
                ? clientOptions.find((cl) => cl.id === selectedClientId)?.label
                : ''
            }
            onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
              setSelectedClientId(newValue?.id ?? '');
            }}
          />
        </Grid>
        <Grid item md={2} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Start date and time
          </Typography>
          <FHAntdDateTimePicker
            data-testid="startDate"
            inputHeight={40}
            coloredBackground
            sx={{ width: '100%', boxShadow: 'none' }}
            format="MM/DD/YYYY h:mm a"
            showTime={{ format: 'h:mm a' }}
            allowClear={false}
            showNow={true}
            use12Hours
            disabled={
              clientAssessment != null &&
              Object.hasOwn(clientAssessment, 'id') &&
              !isAssessmentIncomplete
            }
            disabledDate={(current: Moment) => disabledDatesOutsideRange(current, -14, 0)}
            disabledTime={(current: Moment) => disabledFutureHoursAndMinutes(current)}
            value={startDateTime}
            onBlur={(e: any) => handleUpdateDateTime(moment(new Date(e.target.value)))}
            onOk={(newValue: Moment) =>
              handleUpdateDateTime(moment(newValue)?.seconds(0).milliseconds(0))
            }
            onChange={(value: Moment) =>
              handleDateTimePickerChange(value?.seconds(0).milliseconds(0))
            }
            onSelect={(value: Moment) =>
              handleDateTimePickerChange(value?.seconds(0).milliseconds(0))
            }
          />
        </Grid>
        <Grid item md={1.5} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Duration
          </Typography>
          <FHSelect
            data-testid="duration"
            sx={{ width: '100%', boxShadow: 'none' }}
            inputHeight={40}
            displayEmpty
            backgroundType={BackgroundTypes.Colored}
            disabled={
              clientAssessment != null &&
              Object.hasOwn(clientAssessment, 'id') &&
              !isAssessmentIncomplete
            }
            renderValue={
              !duration
                ? () => (
                    <Typography color={FORTA_GRAY3} fontSize={14}>
                      Select duration
                    </Typography>
                  )
                : undefined
            }
            options={CLIENT_ASSESSMENT_DURATION_OPTIONS}
            value={duration}
            onUpdate={(value: SelectValueType) => {
              const nextDuration = Number(value);
              if (Number.isFinite(nextDuration)) handleUpdateDuration(nextDuration);
            }}
          />
        </Grid>
        <Grid item md={2} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Client location
          </Typography>
          <FHSelect
            data-testid="selectLocation"
            sx={{ width: '100%', boxShadow: 'none' }}
            inputHeight={40}
            displayEmpty
            backgroundType={BackgroundTypes.Colored}
            disabled={
              clientAssessment != null &&
              Object.hasOwn(clientAssessment, 'id') &&
              !isAssessmentIncomplete
            }
            renderValue={
              !clientLocation
                ? () => (
                    <Typography color={FORTA_GRAY3} fontSize={14}>
                      Select client location
                    </Typography>
                  )
                : undefined
            }
            options={SUPERVISION_LOCATION_V2}
            value={clientLocation}
            onUpdate={(value: SelectValueType) => {
              handleUpdateClientLocation(String(value));
            }}
          />
        </Grid>
        <Grid item md={2} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Delivery method
          </Typography>
          <FHSelect
            data-testid="deliveryMethod"
            sx={{ width: '100%', boxShadow: 'none' }}
            inputHeight={40}
            displayEmpty
            backgroundType={BackgroundTypes.Colored}
            disabled={
              clientAssessment != null &&
              Object.hasOwn(clientAssessment, 'id') &&
              !isAssessmentIncomplete
            }
            renderValue={
              !deliveryMethod
                ? () => (
                    <Typography color={FORTA_GRAY3} fontSize={14}>
                      Select delivery method
                    </Typography>
                  )
                : undefined
            }
            options={SUPERVISION_SETTING_V2}
            value={deliveryMethod}
            onUpdate={(value: SelectValueType) => {
              handleUpdateDeliveryMethod(String(value));
            }}
          />
        </Grid>
        <Grid
          item
          md={2}
          sm={6}
          xs={12}
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
          mb={0.5}
        >
          <Box sx={{ display: 'flex', height: '40px' }}>
            <FHButton
              data-testid="assessmentNotesStart"
              sx={{
                width: showDeleteButton ? 'calc(100% - 48px)' : '100%',
                height: '40px',
                mr: showDeleteButton ? 1 : 0,
              }}
              disabled={
                !selectedClientId ||
                !startDateTime ||
                !duration ||
                !clientLocation ||
                !deliveryMethod ||
                clientAssessmentLoading ||
                Boolean(clientAssessment?.id) ||
                buttonDisabled
              }
              onClick={handleSubmit}
            >
              Start notes
            </FHButton>
            {showDeleteButton && (
              <IconButton
                sx={{
                  p: 1,
                  height: '40px',
                  width: '40px',
                  opacity: 0.5,
                  '&:hover': {
                    opacity: 1,
                  },
                }}
                data-testid="deleteClientAssessment"
                onClick={() => setShowConfirmDeleteDialog(true)}
              >
                <TrashIcon color={FORTA_BLUE.primary} width={24} height={24} />
              </IconButton>
            )}
          </Box>
        </Grid>
      </Grid>
      <FHConfirmDialog
        open={showConfirmDeleteDialog}
        title={'Delete assessement'}
        content={'Are you sure you want to delete this assessment? This action cannot be undone.'}
        submittext={'Yes, delete'}
        canceltext={'Cancel'}
        submitBtnTestId={'deleteBtn'}
        cancelBtnTestId={'cancelBtn'}
        onClose={() => setShowConfirmDeleteDialog(false)}
        onSubmit={handleDeleteAssessment}
      />
    </Box>
  );
}
